import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { AuthenticationService } from '@tcc-mono/shared/authentication/tcc-core-auth';
import { BaseService, PaginatedResponse, Response } from '@tcc-mono/shared/data-access';
import { downloadToDisk } from '@tcc-mono/shared/utils';
import {
  PurchaseOrder,
  PurchaseOrderAnimal,
  PurchaseOrderAnimalCustom
} from '@tcc-mono/vandijk/shared/data-access/models';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';


export interface ICreateInvoicesResponse {
  errors: {
    order_id: number;
    order_number: string;
    line_id?: number;
    animal_id?: number;
    error: string;
    source?: string;
    can_override?: boolean;
    override_text?: string;
  }[];
}

export interface TcsMissingAnimal {
  animal_id: number | null;
  country_code: string | null;
  identification_number: string | null;
  missing: string[];
}

export interface TcsMissingAnimalDataError {
  message: string;
  code: 'tcs_missing_animal_data';
  missing_count: number;
  animals: TcsMissingAnimal[];
}

export type PurchaseOrderRvoEnrichEvent =
  | { event: 'start'; total: number }
  | {
      event: 'animal';
      done: number;
      total: number;
      animal_id: number;
      rvo_enriched: boolean;
      error: string | null;
    }
  | { event: 'done'; done: number; total: number; enriched: number; failed: number };

export const isTcsMissingAnimalDataError = (error: unknown): error is HttpErrorResponse => {
  return (error as HttpErrorResponse)?.error?.code === 'tcs_missing_animal_data';
};

export const getTcsMissingAnimalData = (error: unknown): TcsMissingAnimalDataError | null => {
  if (!isTcsMissingAnimalDataError(error)) {
    return null;
  }
  return (error as HttpErrorResponse).error as TcsMissingAnimalDataError;
};

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderService extends BaseService<PurchaseOrder> {
  private readonly _auth = inject(AuthenticationService);

  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/purchase_orders';
  }

  public addAnimal = (orderId: number, animal: PurchaseOrderAnimalCustom): Observable<Response<PurchaseOrderAnimal>> => {
    return this._http.post<Response<PurchaseOrderAnimal>>(`${this.getUrl(this.path)}/${orderId}/animals`, animal);
  }

  public getAnimals = (purchaseOrderId: string | number, params?: Params): Observable<PaginatedResponse<PurchaseOrderAnimal>> => {
    return this.getChild<PaginatedResponse<PurchaseOrderAnimal>>(purchaseOrderId, 'animals', params);
  };

  public brightExport = (id: number): Observable<unknown> => {
    return this.post(null, `${id}/bright_export`);
  };

  public brightDownload = (id: number): Observable<unknown> => {
    const filename = `BS${dayjs().format('YYYYMMDD-HHmmss')}-ink-${id}-VeehandelCorVanDijkbv.xlsx`;

    return this._http.get(this.getUrl(`${this.path}/${id}/bright_export`), { responseType: 'blob' })
      .pipe(
        downloadToDisk(filename)
      );
  };

  public brightExportBulk = (purchaseOrderIds: number[]): Observable<unknown> => {
    return this._http.post(this.getUrl(`${this.path}/bright_export_bulk`),
      { purchase_order_ids: purchaseOrderIds }
    );
  };

  public brightExportBulkDownload = (purchaseOrderIds: number[]): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/bright_export_bulk_download`),
      { purchase_order_ids: purchaseOrderIds },
      { responseType: 'blob' }
    );
  };

  public brightVeevolgExport = (purchaseOrderIds: number[]): Observable<Blob> => {
    return this._http.post(this.getUrl(`${this.path}/bright_veevolg_export`),
      { purchase_order_ids: purchaseOrderIds },
      { responseType: 'blob' }
    );
  };

  public createInvoices = (ids: number[], override: boolean = false): Observable<ICreateInvoicesResponse> => {
    return this.post({ purchase_order_ids: ids, override }, `create_invoices`);
  };

  public updateLine = (orderId: number, orderlineId: number, orderline: PurchaseOrderAnimal): Observable<unknown> => {
    return this._http.put(`${this.getUrl(this.path)}/${orderId}/lines/${orderlineId}`, orderline);
  }

  public deleteLine = (orderId: number, orderlineId: number): Observable<unknown> => {
    return this._http.delete(`${this.getUrl(this.path)}/${orderId}/lines/${orderlineId}`);
  }

  public exportTwaka = (id: number, override: boolean = false): Observable<unknown> => {
    return this.post({ override }, `${id}/twaka_export`);
  }

  public exportTcs = (id: number): Observable<unknown> => {
    return this.post(null, `${id}/tcs_export`);
  }

  public enrichRvo = async (
    id: number,
    onEvent?: (event: PurchaseOrderRvoEnrichEvent) => void
  ): Promise<Extract<PurchaseOrderRvoEnrichEvent, { event: 'done' }>> => {
    const token = this._auth.authResult?.access_token;
    const response = await fetch(this.getUrl(`${this.path}/${id}/enrich-rvo`), {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/x-ndjson'
      }
    });

    if (!response.ok) {
      let message = `RVO-verrijking mislukt (${response.status})`;
      try {
        const body = await response.json();
        message = body?.message || message;
      } catch {
        // ignore non-JSON error bodies
      }
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('Geen response van de server');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let doneEvent: Extract<PurchaseOrderRvoEnrichEvent, { event: 'done' }> | null = null;

    const handleLine = (line: string): void => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }
      const parsed = JSON.parse(trimmed) as PurchaseOrderRvoEnrichEvent;
      onEvent?.(parsed);
      if (parsed.event === 'done') {
        doneEvent = parsed;
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      lines.forEach(handleLine);
    }

    if (buffer.trim()) {
      handleLine(buffer);
    }

    return doneEvent ?? { event: 'done', done: 0, total: 0, enriched: 0, failed: 0 };
  };
}
